import { Link } from "react-router-dom";
import { Star, Clock, Users, Award, CheckCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

// Helper function to format price with INR conversion
const formatPrice = (price) => {
  // Price can be an object {usd, inr} or a string for backward compatibility
  let usd, inr;
  if (typeof price === 'object' && price.usd && price.inr) {
    usd = price.usd;
    inr = price.inr;
  } else {
    // Backward compatibility: extract from string
    const usdAmount = parseInt(price.replace('$', ''));
    usd = usdAmount;
    inr = usdAmount * 90; // Default conversion
  }
  const formattedUSD = `$${usd}`;
  const formattedINR = inr.toLocaleString('en-IN');
  return `${formattedUSD} / ₹${formattedINR}`;
};

export default function CoursesPage() {
  const languages = [
    {
      name: "German",
      flag: "🇩🇪",
      colorClass: "red",
      accentColor: "red",
      levels: [
        {
          level: "A1 - Beginner",
          description:
            "Start your German journey with basic vocabulary, greetings, and essential grammar. Perfect for business introductions and simple workplace conversations.",
          benefits: [
            "Basic business greetings",
            "Simple email communication",
            "Numbers and time expressions",
            "Introduction to German business culture",
          ],
          duration: "6 weeks",
          price: { usd: 99, inr: 9999 },
        },
        {
          level: "A2 - Elementary",
          description:
            "Build confidence in everyday German conversations. Learn to handle routine business tasks and simple negotiations in German-speaking environments.",
          benefits: [
            "Workplace small talk",
            "Basic meeting participation",
            "Simple presentations",
            "German business etiquette",
          ],
          duration: "6 weeks",
          price: { usd: 144, inr: 12999 },
        },
        {
          level: "B1 - Intermediate",
          description:
            "Express opinions and handle complex business situations. Ideal for professionals working with German clients or planning to work in Germany.",
          benefits: [
            "Professional meetings",
            "Complex email writing",
            "Problem-solving discussions",
            "Cultural competency training",
          ],
          duration: "7 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "B2 - Upper Intermediate",
          description:
            "Master detailed business communication and advanced grammar. Handle presentations, negotiations, and complex business correspondence with confidence.",
          benefits: [
            "Advanced presentations",
            "Contract negotiations",
            "Technical discussions",
            "Leadership communication",
          ],
          duration: "8 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "C1 - Advanced",
          description:
            "Achieve near-native proficiency for executive-level communication. Perfect for senior roles, academic discussions, and complex business strategies.",
          benefits: [
            "Executive communication",
            "Strategic planning discussions",
            "Academic and research communication",
            "Cross-cultural leadership",
          ],
          duration: "10 weeks",
          price: { usd: 221, inr: 19999 },
        },
      ],
    },
    {
      name: "Spanish",
      flag: "🇪🇸",
      colorClass: "yellow",
      accentColor: "yellow",
      levels: [
        {
          level: "A1 - Beginner",
          description:
            "Master Spanish basics for international business. Learn essential vocabulary for Latin American and Spanish markets.",
          benefits: [
            "Business introductions",
            "Basic customer service",
            "Travel and logistics",
            "Cultural awareness",
          ],
          duration: "6 weeks",
          price: { usd: 99, inr: 9999 },
        },
        {
          level: "A2 - Elementary",
          description:
            "Develop conversational skills for business environments. Handle routine tasks and simple client interactions in Spanish.",
          benefits: [
            "Client communication",
            "Phone conversations",
            "Simple negotiations",
            "Market terminology",
          ],
          duration: "6 weeks",
          price: { usd: 144, inr: 12999 },
        },
        {
          level: "B1 - Intermediate",
          description:
            "Express complex ideas and participate actively in business meetings. Essential for expanding into Hispanic markets.",
          benefits: [
            "Meeting participation",
            "Project management",
            "Sales presentations",
            "Regional business practices",
          ],
          duration: "7 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "B2 - Upper Intermediate",
          description:
            "Handle advanced business communication across diverse Spanish-speaking regions. Master formal presentations and negotiations.",
          benefits: [
            "Advanced negotiations",
            "Multi-cultural communication",
            "Technical presentations",
            "Strategic discussions",
          ],
          duration: "8 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "C1 - Advanced",
          description:
            "Achieve executive-level Spanish for senior management roles. Perfect for international leadership positions.",
          benefits: [
            "Executive leadership",
            "Strategic planning",
            "Academic research",
            "Cross-border mergers",
          ],
          duration: "10 weeks",
          price: { usd: 221, inr: 19999 },
        },
      ],
    },
    {
      name: "Japanese",
      flag: "🇯🇵",
      colorClass: "pink",
      accentColor: "pink",
      levels: [
        {
          level: "N5 - Basic",
          description:
            "Foundation Japanese for business professionals. Learn hiragana, katakana, and essential business vocabulary for the Japanese market.",
          benefits: [
            "Basic business cards exchange",
            "Simple greetings and introductions",
            "Numbers and basic transactions",
            "Japanese business etiquette fundamentals",
          ],
          duration: "6 weeks",
          price: { usd: 99, inr: 9999 },
        },
        {
          level: "N4 - Elementary",
          description:
            "Build practical communication skills for Japanese business environments. Understand basic workplace conversations and procedures.",
          benefits: [
            "Workplace conversations",
            "Simple meeting participation",
            "Email basics",
            "Cultural sensitivity training",
          ],
          duration: "6 weeks",
          price: { usd: 144, inr: 12999 },
        },
        {
          level: "N3 - Intermediate",
          description:
            "Develop intermediate Japanese for professional settings. Handle complex business situations and technical discussions.",
          benefits: [
            "Technical discussions",
            "Project coordination",
            "Client presentations",
            "Advanced business etiquette",
          ],
          duration: "7 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "N2 - Upper Intermediate",
          description:
            "Master advanced Japanese for business leadership. Essential for management positions in Japanese corporations.",
          benefits: [
            "Leadership communication",
            "Strategic planning",
            "Complex negotiations",
            "Academic and research discussions",
          ],
          duration: "8 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "N1 - Advanced",
          description:
            "Achieve near-native Japanese proficiency. Perfect for senior executive roles and academic research in Japanese institutions.",
          benefits: [
            "Executive leadership",
            "Academic research",
            "Cultural integration",
            "Complex technical communication",
          ],
          duration: "10 weeks",
          price: { usd: 221, inr: 19999 },
        },
      ],
    },
    {
      name: "Korean",
      flag: "🇰🇷",
      colorClass: "blue",
      accentColor: "blue",
      levels: [
        {
          level: "TOPIK 1 - Beginner",
          description:
            "Start Korean for business expansion into one of Asia's most dynamic markets. Learn Hangul and basic business vocabulary.",
          benefits: [
            "Basic business introductions",
            "Simple customer service",
            "Technology sector terminology",
            "Korean business hierarchy",
          ],
          duration: "6 weeks",
          price: { usd: 99, inr: 9999 },
        },
        {
          level: "TOPIK 2 - Elementary",
          description:
            "Build conversational Korean for professional environments. Essential for tech companies and manufacturing businesses.",
          benefits: [
            "Workplace communication",
            "Basic presentations",
            "Manufacturing terminology",
            "Tech industry basics",
          ],
          duration: "6 weeks",
          price: { usd: 144, inr: 12999 },
        },
        {
          level: "TOPIK 3 - Intermediate",
          description:
            "Develop intermediate Korean for complex business operations. Handle negotiations and technical discussions effectively.",
          benefits: [
            "Business negotiations",
            "Technical discussions",
            "Project management",
            "Supply chain communication",
          ],
          duration: "7 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "TOPIK 4 - Upper Intermediate",
          description:
            "Master advanced Korean for leadership roles in Korean corporations. Essential for international business development.",
          benefits: [
            "Leadership roles",
            "Strategic planning",
            "International trade",
            "Cultural business practices",
          ],
          duration: "8 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "TOPIK 5 - Advanced",
          description:
            "Achieve advanced Korean proficiency for senior management. Perfect for joint ventures and academic collaborations.",
          benefits: [
            "Senior management",
            "Academic collaboration",
            "Joint ventures",
            "Government relations",
          ],
          duration: "10 weeks",
          price: { usd: 221, inr: 19999 },
        },
        {
          level: "TOPIK 6 - Expert",
          description:
            "Reach expert-level Korean for executive positions and academic research. The highest level of professional Korean competency.",
          benefits: [
            "Executive leadership",
            "Academic research",
            "Government diplomacy",
            "Cultural mastery",
          ],
          duration: "10 weeks",
          price: { usd: 221, inr: 19999 },
        },
      ],
    },
    {
      name: "French",
      flag: "🇫🇷",
      colorClass: "indigo",
      accentColor: "indigo",
      levels: [
        {
          level: "A1 - Beginner",
          description:
            "Begin French for international business and diplomacy. Essential for African markets, Canadian business, and European operations.",
          benefits: [
            "Business introductions",
            "Basic customer service",
            "Travel and logistics",
            "French business culture",
          ],
          duration: "6 weeks",
          price: { usd: 99, inr: 9999 },
        },
        {
          level: "A2 - Elementary",
          description:
            "Develop practical French for business environments. Handle routine communications and simple client interactions.",
          benefits: [
            "Client communication",
            "Email correspondence",
            "Meeting basics",
            "Cultural sensitivity",
          ],
          duration: "6 weeks",
          price: { usd: 144, inr: 12999 },
        },
        {
          level: "B1 - Intermediate",
          description:
            "Express complex business ideas and participate in French business culture. Essential for European and African market expansion.",
          benefits: [
            "Meeting participation",
            "Business presentations",
            "Contract discussions",
            "Regional market knowledge",
          ],
          duration: "7 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "B2 - Upper Intermediate",
          description:
            "Handle sophisticated business communication and negotiations. Perfect for management roles in French-speaking regions.",
          benefits: [
            "Advanced negotiations",
            "Management communication",
            "Technical presentations",
            "Strategic planning",
          ],
          duration: "8 weeks",
          price: { usd: 177, inr: 15999 },
        },
        {
          level: "C1 - Advanced",
          description:
            "Achieve executive-level French for senior leadership roles. Essential for diplomatic relations and international organizations.",
          benefits: [
            "Executive leadership",
            "Diplomatic communication",
            "Academic research",
            "International organizations",
          ],
          duration: "10 weeks",
          price: { usd: 221, inr: 19999 },
        },
      ],
    },
  ];

  return (
    <div className="courses-page">
      <Header />

      {/* Page Header */}
      <section className="page-header">
        <div className="header-content">
          <h1><span className="first-word">Our Courses</span> </h1>
          <p>Professional language training designed for global business success</p>
        </div>
      </section>

      {/* Course Introduction */}
      <section className="intro-section">
        <div className="section-content">
          <h2>Structured Learning Paths for Every Level</h2>
          <p>
            Our comprehensive language courses are designed by certified
            linguists and industry experts. Each course focuses on real-world
            workplace communication, cultural awareness, and practical
            business skills.
          </p>
          <div className="features-grid">
            <div className="feature-badge blue">
              <Users style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <span>Live Classes</span>
            </div>
            <div className="feature-badge yellow">
              <Award style={{ width: '1.5rem', height: '1.5rem', color: '#ca8a04' }} />
              <span>Certified Instructors</span>
            </div>
            <div className="feature-badge green">
              <CheckCircle style={{ width: '1.5rem', height: '1.5rem', color: '#16a34a' }} />
              <span>Business Focused</span>
            </div>
            <div className="feature-badge purple">
              <Clock style={{ width: '1.5rem', height: '1.5rem', color: '#9333ea' }} />
              <span>Flexible Schedule</span>
            </div>
          </div>
        </div>
      </section>

      {/* Languages and Courses */}
      {languages.map((language, langIndex) => (
        <section
          key={langIndex}
          id={language.name.toLowerCase()}
          className={`language-section ${langIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
        >
          <div className="section-content">
            <div className="section-header">
              <div className="flag">{language.flag}</div>
              <h2>{language.name} Language Courses</h2>
              <p>Master {language.name} for international business success</p>
            </div>

            <div className="courses-grid">
              {language.levels.map((course, courseIndex) => (
                <div
                  key={courseIndex}
                  className={`course-card ${language.colorClass}`}
                >
                  <div className="course-header">
                    <h3>{course.level}</h3>
                    <div className="stars">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} style={{ width: '1rem', height: '1rem', color: '#eab308', fill: '#eab308' }} />
                      ))}
                    </div>
                  </div>

                  <p>{course.description}</p>

                  <div className="benefits">
                    <h4>Key Benefits:</h4>
                    <ul>
                      {course.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex}>
                          <CheckCircle className="check-icon" style={{ width: '1rem', height: '1rem', color: '#22c55e' }} />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="course-footer">
                    <div className="duration">
                      <Clock className="clock-icon" style={{ width: '1rem', height: '1rem' }} />
                      <span>{course.duration}</span>
                    </div>
                    <div className={`price ${language.accentColor}`}>
                      {formatPrice(course.price)}
                    </div>
                  </div>

                  <Link to="/contact#contact-form" className="btn btn-primary btn-full">Enroll Now</Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Course Features */}
      <section className="features-section">
        <div className="section-content">
          <div className="section-header">
            <h2>What's Included in Every Course</h2>
            <p>Comprehensive learning experience designed for busy professionals</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="icon-wrapper blue">
                <Users style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />
              </div>
              <h3>Live Interactive Classes</h3>
              <p>Small group sessions with real-time feedback and practice</p>
            </div>
            <div className="feature-card">
              <div className="icon-wrapper yellow">
                <Award style={{ width: '2rem', height: '2rem', color: '#ca8a04' }} />
              </div>
              <h3>Certification</h3>
              <p>Official certificate upon course completion</p>
            </div>
            <div className="feature-card">
              <div className="icon-wrapper green">
                <CheckCircle style={{ width: '2rem', height: '2rem', color: '#16a34a' }} />
              </div>
              <h3>Business Materials</h3>
              <p>Real-world business scenarios and case studies</p>
            </div>
            <div className="feature-card">
              <div className="icon-wrapper purple">
                <Clock style={{ width: '2rem', height: '2rem', color: '#9333ea' }} />
              </div>
              <h3>Flexible Scheduling</h3>
              <p>Multiple time slots to fit your busy schedule</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-content">
          <h2>Ready to Start Learning?</h2>
          <p>
            Choose your language and level to begin your professional
            development journey
          </p>
          <div className="cta-buttons">
            <Link to="/contact#contact-form" className="btn btn-primary btn-lg">
              Enroll Now
            </Link>
            <Link to="/contact#contact-form" className="btn btn-secondary btn-lg">
              Get Free Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}